import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './image-editor-modal.html';
import type { DocumentFile } from '../services/api.types';
import { replaceFile } from '../services/api.service';
import { ConfirmDialog } from './confirm-dialog';
import { apiUrl } from '../services/runtime-config';

type Point = { x: number; y: number };
type Rect = { x: number; y: number; width: number; height: number };

@customElement({ name: 'image-editor-modal', template })
export class ImageEditorModal {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public file!: DocumentFile;
  @bindable public saved!: (updatedFile: DocumentFile) => void;
  @bindable public closed!: () => void;

  public loading = true;
  public saving = false;
  public error = '';
  public originalFileSize = 0;
  public currentFileSize = 0;
  public originalWidth = 0;
  public originalHeight = 0;
  public currentWidth = 0;
  public currentHeight = 0;
  public keepAspectRatio = true;
  public resizeWidth = '';
  public resizeHeight = '';
  public cropMode = false;
  public cropSelection: Rect | null = null;

  private canvasRef!: HTMLCanvasElement;
  private readonly originalCanvas = document.createElement('canvas');
  private workingCanvas = document.createElement('canvas');
  private dragStart: Point | null = null;
  private dragCurrent: Point | null = null;
  private activePointerId: number | null = null;
  private _keyHandler!: (e: KeyboardEvent) => void;
  private dirty = false;

  public get hasChanges(): boolean {
    return this.dirty;
  }

  public async attached(): Promise<void> {
    this._keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        void this.close();
      }
    };
    document.addEventListener('keydown', this._keyHandler);
    await this.loadImage();
  }

  public detached(): void {
    document.removeEventListener('keydown', this._keyHandler);
  }

  public async loadImage(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      const response = await fetch(apiUrl(`/documents/${this.file.documentId}/files/${this.file.id}/download`));
      if (!response.ok) {
        throw new Error(`Failed to load ${this.file.fileName}`);
      }

      const blob = await response.blob();
      const image = await this.blobToImage(blob);

      this.originalFileSize = blob.size;
      this.currentFileSize = blob.size;
      this.originalWidth = image.naturalWidth;
      this.originalHeight = image.naturalHeight;
      this.currentWidth = image.naturalWidth;
      this.currentHeight = image.naturalHeight;
      this.resizeWidth = String(image.naturalWidth);
      this.resizeHeight = String(image.naturalHeight);

      this.originalCanvas.width = image.naturalWidth;
      this.originalCanvas.height = image.naturalHeight;
      const originalCtx = this.originalCanvas.getContext('2d');
      if (!originalCtx) throw new Error('Canvas unavailable.');
      originalCtx.drawImage(image, 0, 0);

      this.workingCanvas = this.cloneCanvas(this.originalCanvas);
      this.dirty = false;
      this.render();
      void this.updateCurrentFileSize();
    } catch (err: unknown) {
      this.error = err instanceof Error ? err.message : this.i18n.tr('documents.imageEditor.loadFailed');
    } finally {
      this.loading = false;
      setTimeout(() => this.render(), 0);
    }
  }

  public async close(): Promise<void> {
    if (this.dirty) {
      const discard = await ConfirmDialog.confirm(
        this.i18n.tr('documents.form.unsavedChanges'),
        this.i18n.tr('documents.form.unsavedChangesTitle'),
      );
      if (!discard) return;
    }
    this.closed();
  }

  public resetChanges(): void {
    this.workingCanvas = this.cloneCanvas(this.originalCanvas);
    this.currentFileSize = this.originalFileSize;
    this.currentWidth = this.originalWidth;
    this.currentHeight = this.originalHeight;
    this.resizeWidth = String(this.originalWidth);
    this.resizeHeight = String(this.originalHeight);
    this.cropMode = false;
    this.cropSelection = null;
    this.dragStart = null;
    this.dragCurrent = null;
    this.activePointerId = null;
    this.dirty = false;
    this.render();
    void this.updateCurrentFileSize();
  }

  public rotateClockwise(): void {
    this.transformCanvas(next => {
      const ctx = next.getContext('2d');
      if (!ctx) return;
      next.width = this.workingCanvas.height;
      next.height = this.workingCanvas.width;
      ctx.translate(next.width, 0);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(this.workingCanvas, 0, 0);
    });
  }

  public rotateCounterClockwise(): void {
    this.transformCanvas(next => {
      const ctx = next.getContext('2d');
      if (!ctx) return;
      next.width = this.workingCanvas.height;
      next.height = this.workingCanvas.width;
      ctx.translate(0, next.height);
      ctx.rotate(-Math.PI / 2);
      ctx.drawImage(this.workingCanvas, 0, 0);
    });
  }

  public onResizeWidthInput(): void {
    if (!this.keepAspectRatio) return;
    const width = this.parsePositiveInt(this.resizeWidth);
    if (!width || !this.currentWidth) return;
    this.resizeHeight = String(Math.max(1, Math.round(width * this.currentHeight / this.currentWidth)));
  }

  public onResizeHeightInput(): void {
    if (!this.keepAspectRatio) return;
    const height = this.parsePositiveInt(this.resizeHeight);
    if (!height || !this.currentHeight) return;
    this.resizeWidth = String(Math.max(1, Math.round(height * this.currentWidth / this.currentHeight)));
  }

  public applyResize(): void {
    const width = this.parsePositiveInt(this.resizeWidth);
    const height = this.parsePositiveInt(this.resizeHeight);
    if (!width || !height) return;
    if (width === this.currentWidth && height === this.currentHeight) return;

    this.transformCanvas(next => {
      const ctx = next.getContext('2d');
      if (!ctx) return;
      next.width = width;
      next.height = height;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(this.workingCanvas, 0, 0, width, height);
    });
  }

  public beginCrop(): void {
    this.cropMode = true;
    this.cropSelection = null;
    this.dragStart = null;
    this.dragCurrent = null;
    this.activePointerId = null;
    this.render();
  }

  public onCanvasPointerDown(event: PointerEvent): void {
    if (!this.cropMode) return;
    if (event.button !== 0) return;
    const point = this.toCanvasPoint(event);
    if (!point) return;
    this.activePointerId = event.pointerId;
    this.dragStart = point;
    this.dragCurrent = point;
    this.cropSelection = null;
    this.capturePointer(event);
    event.preventDefault();
    this.render();
  }

  public onCanvasPointerMove(event: PointerEvent): void {
    if (!this.cropMode || !this.dragStart || this.activePointerId !== event.pointerId) return;
    this.dragCurrent = this.toCanvasPoint(event);
    event.preventDefault();
    this.render();
  }

  public onCanvasPointerUp(event: PointerEvent): void {
    if (!this.cropMode || !this.dragStart || this.activePointerId !== event.pointerId) return;
    this.dragCurrent = this.toCanvasPoint(event);
    const rect = this.normalizeRect(this.dragStart, this.dragCurrent ?? this.dragStart);
    this.dragStart = null;
    this.dragCurrent = null;
    this.releasePointer(event);
    this.activePointerId = null;
    this.cropSelection = rect.width > 1 && rect.height > 1 ? rect : null;
    event.preventDefault();
    this.render();
  }

  public onCanvasPointerCancel(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId) return;
    this.dragStart = null;
    this.dragCurrent = null;
    this.releasePointer(event);
    this.activePointerId = null;
    event.preventDefault();
    this.render();
  }

  public clearCrop(): void {
    this.cropSelection = null;
    this.cropMode = false;
    this.dragStart = null;
    this.dragCurrent = null;
    this.activePointerId = null;
    this.render();
  }

  public applyCrop(): void {
    if (!this.cropSelection) return;
    const rect = this.cropSelection;
    this.transformCanvas(next => {
      const ctx = next.getContext('2d');
      if (!ctx) return;
      next.width = rect.width;
      next.height = rect.height;
      ctx.drawImage(this.workingCanvas, rect.x, rect.y, rect.width, rect.height, 0, 0, rect.width, rect.height);
    });
    this.cropMode = false;
    this.cropSelection = null;
    this.activePointerId = null;
  }

  public async save(): Promise<void> {
    this.saving = true;
    this.error = '';
    try {
      const output = await this.exportFile();
      const updated = await replaceFile(this.file.documentId, this.file.id, output);
      if (updated.duplicateSkipped) {
        await ConfirmDialog.alert(
          this.i18n.tr('documents.form.duplicateFileSkippedWarning', {
            fileName: updated.fileName,
            duplicateFileName: updated.duplicateFileName ?? updated.fileName,
            documentTitle: updated.duplicateDocumentTitle ?? '',
          }),
          this.i18n.tr('documents.form.duplicateFileTitle'),
        );
        return;
      }
      this.dirty = false;
      this.saved(updated);
      this.closed();
    } catch (err: unknown) {
      this.error = err instanceof Error ? err.message : this.i18n.tr('documents.form.saveFailed');
    } finally {
      this.saving = false;
    }
  }

  public isCropReady(): boolean {
    return this.cropSelection !== null;
  }

  public formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private async exportFile(): Promise<File> {
    const mimeType = this.getExportMimeType();
    const blob = await this.canvasToBlob(mimeType);
    const fileName = this.getExportFileName(mimeType);
    return new File([blob], fileName, { type: mimeType });
  }

  private async canvasToBlob(mimeType: string): Promise<Blob> {
    const blob = await new Promise<Blob>((resolve, reject) => {
      const quality = mimeType === 'image/jpeg' ? 0.92 : undefined;
      this.workingCanvas.toBlob(result => {
        if (result) resolve(result);
        else reject(new Error('Failed to export edited image.'));
      }, mimeType, quality);
    });
    return blob;
  }

  private getExportMimeType(): string {
    const ct = (this.file.contentType || '').toLowerCase();
    if (ct === 'image/jpeg' || ct === 'image/png' || ct === 'image/webp') return ct;
    return 'image/png';
  }

  private getExportFileName(mimeType: string): string {
    if (mimeType === (this.file.contentType || '').toLowerCase()) {
      return this.file.fileName;
    }
    const ext = mimeType === 'image/jpeg' ? 'jpg'
      : mimeType === 'image/webp' ? 'webp'
      : 'png';
    const base = this.file.fileName.includes('.')
      ? this.file.fileName.slice(0, this.file.fileName.lastIndexOf('.'))
      : this.file.fileName;
    return `${base}.${ext}`;
  }

  private transformCanvas(transform: (next: HTMLCanvasElement) => void): void {
    const next = document.createElement('canvas');
    transform(next);
    this.workingCanvas = next;
    this.currentWidth = next.width;
    this.currentHeight = next.height;
    this.resizeWidth = String(next.width);
    this.resizeHeight = String(next.height);
    this.cropSelection = null;
    this.dragStart = null;
    this.dragCurrent = null;
    this.dirty = true;
    this.render();
    void this.updateCurrentFileSize();
  }

  private render(): void {
    if (!this.canvasRef) return;
    this.canvasRef.width = this.workingCanvas.width;
    this.canvasRef.height = this.workingCanvas.height;
    const ctx = this.canvasRef.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, this.canvasRef.width, this.canvasRef.height);
    ctx.drawImage(this.workingCanvas, 0, 0);

    const rect = this.dragStart && this.dragCurrent
      ? this.normalizeRect(this.dragStart, this.dragCurrent)
      : this.cropSelection;
    if (!rect) return;

    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
    ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
    ctx.strokeStyle = '#60a5fa';
    ctx.lineWidth = 2;
    ctx.strokeRect(rect.x + 1, rect.y + 1, Math.max(0, rect.width - 2), Math.max(0, rect.height - 2));
    ctx.restore();
  }

  private toCanvasPoint(event: PointerEvent): Point | null {
    if (!this.canvasRef) return null;
    const bounds = this.canvasRef.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return null;
    const x = (event.clientX - bounds.left) * this.canvasRef.width / bounds.width;
    const y = (event.clientY - bounds.top) * this.canvasRef.height / bounds.height;
    return {
      x: Math.min(this.canvasRef.width, Math.max(0, x)),
      y: Math.min(this.canvasRef.height, Math.max(0, y)),
    };
  }

  private capturePointer(event: PointerEvent): void {
    if (!this.canvasRef || this.canvasRef.hasPointerCapture(event.pointerId)) return;
    this.canvasRef.setPointerCapture(event.pointerId);
  }

  private releasePointer(event: PointerEvent): void {
    if (!this.canvasRef || !this.canvasRef.hasPointerCapture(event.pointerId)) return;
    this.canvasRef.releasePointerCapture(event.pointerId);
  }

  private normalizeRect(start: Point, end: Point): Rect {
    const x = Math.max(0, Math.min(start.x, end.x));
    const y = Math.max(0, Math.min(start.y, end.y));
    const width = Math.min(this.workingCanvas.width - x, Math.abs(end.x - start.x));
    const height = Math.min(this.workingCanvas.height - y, Math.abs(end.y - start.y));
    return {
      x: Math.round(x),
      y: Math.round(y),
      width: Math.max(0, Math.round(width)),
      height: Math.max(0, Math.round(height)),
    };
  }

  private parsePositiveInt(value: string): number | null {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private async updateCurrentFileSize(): Promise<void> {
    try {
      const blob = await this.canvasToBlob(this.getExportMimeType());
      this.currentFileSize = blob.size;
    } catch {
      this.currentFileSize = 0;
    }
  }

  private async blobToImage(blob: Blob): Promise<HTMLImageElement> {
    const objectUrl = URL.createObjectURL(blob);
    try {
      const image = new Image();
      image.decoding = 'async';
      image.src = objectUrl;
      await image.decode();
      return image;
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  private cloneCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
    const next = document.createElement('canvas');
    next.width = source.width;
    next.height = source.height;
    const ctx = next.getContext('2d');
    if (ctx) {
      ctx.drawImage(source, 0, 0);
    }
    return next;
  }
}
